import axios from 'axios';

/**
 * Client for the invoice API endpoints
 */
class InvoiceClient {
  constructor(backendUrl) {
    this.backendUrl = backendUrl;
  }

  /**
   * Standard headers required for every invoice request
   */
  getHeaders() {
    return {
      'X-User-Id': localStorage.getItem('userId')
    };
  }

  /**
   * Get invoices for a company, with optional filters
   */
  async getInvoices(companyId, { recipientUserId, status, startDate, endDate, page, pageSize, search, sortBy, sortDir } = {}) {
    const params = { companyId };
    if (recipientUserId) params.recipientUserId = recipientUserId;
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (page != null) params.page = page;
    if (pageSize != null) params.pageSize = pageSize;
    if (search) params.search = search;
    if (sortBy) params.sortBy = sortBy;
    if (sortDir) params.sortDir = sortDir;

    return axios.get(`${this.backendUrl}/invoices`, {
      params,
      headers: this.getHeaders()
    });
  }

  /**
   * Get the company's live inventory options for custom invoice lines
   */
  async getInventoryItems(companyId) {
    return axios.get(`${this.backendUrl}/invoices/inventory-items`, {
      params: { companyId },
      headers: this.getHeaders()
    });
  }

  /**
   * Preview an order-based invoice without creating it
   */
  async previewInvoice({ companyId, recipientUserId, periodStart, periodEnd }) {
    return axios.post(`${this.backendUrl}/invoices/preview`, {
      companyId,
      recipientUserId,
      periodStart,
      periodEnd
    }, {
      headers: this.getHeaders()
    });
  }

  /**
   * Create a new draft invoice
   */
  async createInvoice(invoiceData) {
    return axios.post(`${this.backendUrl}/invoices`, invoiceData, {
      headers: this.getHeaders()
    });
  }

  /**
   * Get a single invoice
   */
  async getInvoice(invoiceId) {
    return axios.get(`${this.backendUrl}/invoices/${invoiceId}`, {
      headers: this.getHeaders()
    });
  }

  /**
   * Update a draft invoice
   */
  async updateInvoice(invoiceId, updates) {
    return axios.put(`${this.backendUrl}/invoices/${invoiceId}`, updates, {
      headers: this.getHeaders()
    });
  }

  /**
   * Revise an issued invoice (recomputes totals and regenerates the PDF)
   */
  async reviseInvoice(invoiceId, updates) {
    return axios.post(`${this.backendUrl}/invoices/${invoiceId}/revise`, updates, {
      headers: this.getHeaders()
    });
  }

  /**
   * Issue a draft invoice
   */
  async issueInvoice(invoiceId) {
    return axios.post(`${this.backendUrl}/invoices/${invoiceId}/issue`, {}, {
      headers: this.getHeaders()
    });
  }

  /**
   * Email an issued invoice to its recipient (PDF attached, via Resend)
   */
  async sendInvoiceEmail(invoiceId, emailData) {
    return axios.post(`${this.backendUrl}/invoices/${invoiceId}/send-email`, emailData, {
      headers: this.getHeaders()
    });
  }

  /**
   * Send a payment reminder for an issued invoice
   */
  async remindInvoice(invoiceId) {
    return axios.post(`${this.backendUrl}/invoices/${invoiceId}/remind`, {}, {
      headers: this.getHeaders()
    });
  }

  /**
   * Transition an issued invoice to paid or void
   */
  async setInvoiceStatus(invoiceId, status) {
    return axios.post(`${this.backendUrl}/invoices/${invoiceId}/status`, { status }, {
      headers: this.getHeaders()
    });
  }

  /**
   * Delete a draft or issued invoice
   */
  async deleteInvoice(invoiceId) {
    return axios.delete(`${this.backendUrl}/invoices/${invoiceId}`, {
      headers: this.getHeaders()
    });
  }

  /**
   * Download the PDF for an issued invoice as a blob
   */
  async downloadPdf(invoiceId) {
    return axios.get(`${this.backendUrl}/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
      headers: this.getHeaders()
    });
  }

  /**
   * Get the carrier-neutral shipping label as a blob for printing or download
   */
  async getShippingLabel(invoiceId, download = false) {
    const options = {
      responseType: 'blob',
      headers: this.getHeaders()
    };
    if (download) options.params = { download: 1 };
    return axios.get(`${this.backendUrl}/invoices/${invoiceId}/shipping-label`, options);
  }

  /**
   * Preview a draft invoice's PDF as a blob, before it's issued
   */
  async previewInvoicePdf(invoiceId) {
    return axios.get(`${this.backendUrl}/invoices/${invoiceId}/pdf/preview`, {
      responseType: 'blob',
      headers: this.getHeaders()
    });
  }

  /**
   * Get the logged-in user's own non-draft invoices, with optional filters
   */
  async getMyInvoices({ status, startDate, endDate, sortBy, sortDir, page, pageSize } = {}) {
    const params = {};
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (sortBy) params.sortBy = sortBy;
    if (sortDir) params.sortDir = sortDir;
    if (page != null) params.page = page;
    if (pageSize != null) params.pageSize = pageSize;

    return axios.get(`${this.backendUrl}/invoices/my`, {
      params,
      headers: this.getHeaders()
    });
  }

  /**
   * Download the PDF for one of the logged-in user's own invoices as a blob
   */
  async downloadMyInvoicePdf(invoiceId) {
    return axios.get(`${this.backendUrl}/invoices/my/${invoiceId}/pdf`, {
      responseType: 'blob',
      headers: this.getHeaders()
    });
  }

  /**
   * List invoice-only contacts for a company
   */
  async getInvoiceContacts(companyId) {
    return axios.get(`${this.backendUrl}/invoice-contacts`, {
      params: { companyId },
      headers: this.getHeaders()
    });
  }

  /**
   * Create a new invoice-only contact
   */
  async createInvoiceContact(contactData) {
    return axios.post(`${this.backendUrl}/invoice-contacts`, contactData, {
      headers: this.getHeaders()
    });
  }

  /**
   * Update an invoice-only contact
   */
  async updateInvoiceContact(contactId, updates) {
    return axios.put(`${this.backendUrl}/invoice-contacts/${contactId}`, updates, {
      headers: this.getHeaders()
    });
  }

  /**
   * Delete an invoice-only contact
   */
  async deleteInvoiceContact(contactId) {
    return axios.delete(`${this.backendUrl}/invoice-contacts/${contactId}`, {
      headers: this.getHeaders()
    });
  }

  /**
   * List saved invoice note templates for a company
   */
  async getInvoiceNoteTemplates(companyId) {
    return axios.get(`${this.backendUrl}/invoice-note-templates`, {
      params: { companyId },
      headers: this.getHeaders()
    });
  }

  /**
   * Save a named invoice note template
   */
  async createInvoiceNoteTemplate(templateData) {
    return axios.post(`${this.backendUrl}/invoice-note-templates`, templateData, {
      headers: this.getHeaders()
    });
  }

  /**
   * Rename or edit a saved invoice note template
   */
  async updateInvoiceNoteTemplate(templateId, updates) {
    return axios.put(`${this.backendUrl}/invoice-note-templates/${templateId}`, updates, {
      headers: this.getHeaders()
    });
  }

  /**
   * Delete a saved invoice note template
   */
  async deleteInvoiceNoteTemplate(templateId) {
    return axios.delete(`${this.backendUrl}/invoice-note-templates/${templateId}`, {
      headers: this.getHeaders()
    });
  }
}

export default InvoiceClient;
